import { Link } from "react-router-dom"
import {ArrowRight, Target, Heart, Lightbulb, Shield, Users, Award, TrendingUp, Handshake} from 'lucide-react'
import SectionHeading from "@/components/SectionHeading"
import InlineEdit from "@/components/InlineEdit"

const values = [
  { icon: Heart, key: "about_val_1", title: "Proximité", desc: "Un interlocuteur unique qui connaît votre entreprise et vos enjeux. Pas de centre d'appel anonyme." },
  { icon: Lightbulb, key: "about_val_2", title: "Expertise", desc: "Une équipe certifiée et formée en continu aux dernières technologies et bonnes pratiques." },
  { icon: Shield, key: "about_val_3", title: "Fiabilité", desc: "Des engagements clairs, des délais respectés et une transparence totale sur nos interventions." },
  { icon: TrendingUp, key: "about_val_4", title: "Proactivité", desc: "Nous anticipons les problèmes avant qu'ils ne surviennent grâce à une surveillance continue." },
  { icon: Handshake, key: "about_val_5", title: "Partenariat", desc: "Nous construisons des relations durables, pas des contrats jetables. Votre réussite est la nôtre." },
  { icon: Award, key: "about_val_6", title: "Qualité", desc: "Chaque intervention est documentée, chaque solution est testée. Nous visons l'excellence." },
]

const timeline = [
  { key: "about_tl_1", year: "2018", title: "Création de LJJ IT", desc: "Lancement de l'activité avec une vision claire : simplifier l'IT pour les PME." },
  { key: "about_tl_2", year: "2019", title: "Premiers contrats de maintenance", desc: "Mise en place de contrats récurrents avec des entreprises locales." },
  { key: "about_tl_3", year: "2021", title: "Expansion de l'équipe", desc: "Recrutement de techniciens spécialisés pour couvrir plus de domaines." },
  { key: "about_tl_4", year: "2023", title: "Lancement du portail support", desc: "Déploiement du système de ticketing pour un suivi structuré des demandes." },
  { key: "about_tl_5", year: "2025", title: "500+ interventions / an", desc: "LJJ IT accompagne désormais des dizaines d'entreprises au quotidien." },
]

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-24 lg:pt-32">
      {/* Header */}
      <section className="section-padding pb-16">
        <SectionHeading
          badge="À propos"
          title="L'équipe derrière"
          highlight="LJJ IT"
          description="Nous sommes une équipe passionnée par la technologie et engagée à fournir un service informatique d'excellence aux PME et indépendants."
        />
      </section>

      {/* Mission */}
      <section className="section-padding py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-6 h-6 text-cyan-400" />
              <InlineEdit contentKey="about_mission_title" defaultValue="Notre mission" as="h3" className="text-2xl font-bold text-white" />
            </div>
            <div className="mb-4">
              <InlineEdit
                contentKey="about_mission_p1"
                defaultValue="Trop d'entreprises subissent leur informatique au lieu de s'en servir comme levier de croissance. Chez LJJ IT, nous croyons que chaque PME mérite un support informatique professionnel, réactif et abordable."
                as="p"
                className="text-slate-400 leading-relaxed"
                multiline
              />
            </div>
            <InlineEdit
              contentKey="about_mission_p2"
              defaultValue="Notre mission est de centraliser, structurer et simplifier la gestion IT de nos clients, pour qu'ils puissent se concentrer sur ce qui compte vraiment : leur métier."
              as="p"
              className="text-slate-400 leading-relaxed"
              multiline
            />
          </div>

          <div className="card !p-8 bg-gradient-to-br from-cyan-500/5 to-emerald-500/5">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-emerald-400" />
              <InlineEdit contentKey="about_who_title" defaultValue="Qui sommes-nous ?" as="h3" className="text-xl font-bold text-white" />
            </div>
            <InlineEdit
              contentKey="about_who_desc"
              defaultValue="LJJ IT est une entreprise de services informatiques basée à Lausanne, en Suisse. Nous accompagnons les PME, indépendants et associations dans la gestion de leur parc informatique, leur infrastructure et leur support technique."
              as="p"
              className="text-slate-400 text-sm leading-relaxed mb-4"
              multiline
            />
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="text-center p-4 rounded-xl bg-slate-900/50 border border-slate-800/50">
                <div className="text-2xl font-bold gradient-text">
                  <InlineEdit contentKey="about_stat_1_val" defaultValue="7+" className="text-2xl font-bold" />
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  <InlineEdit contentKey="about_stat_1_label" defaultValue="Années d'expérience" className="text-xs text-slate-500" />
                </div>
              </div>
              <div className="text-center p-4 rounded-xl bg-slate-900/50 border border-slate-800/50">
                <div className="text-2xl font-bold gradient-text">
                  <InlineEdit contentKey="about_stat_2_val" defaultValue="50+" className="text-2xl font-bold" />
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  <InlineEdit contentKey="about_stat_2_label" defaultValue="Clients accompagnés" className="text-xs text-slate-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding py-20 lg:py-28">
        <SectionHeading
          badge="Nos valeurs"
          title="Ce qui nous"
          highlight="différencie"
          description="Nos valeurs guident chacune de nos interventions et définissent notre façon de travailler avec vous."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {values.map((v, i) => (
            <div
              key={v.key}
              className="card-hover-lift group animate-fade-in-up"
              style={{ animationDelay: `${0.05 * i}s` }}
            >
              <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                <v.icon className="w-5 h-5 text-cyan-400" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">
                <InlineEdit contentKey={`${v.key}_title`} defaultValue={v.title} className="text-lg font-semibold text-white" />
              </h4>
              <InlineEdit contentKey={`${v.key}_desc`} defaultValue={v.desc} as="p" className="text-sm text-slate-400 leading-relaxed" multiline />
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding py-20 lg:py-28 border-t border-slate-800/50">
        <SectionHeading
          badge="Notre parcours"
          title="L'histoire de"
          highlight="LJJ IT"
        />

        <div className="max-w-2xl mx-auto">
          {timeline.map((item, i) => (
            <div key={item.key} className="flex gap-6 pb-10 last:pb-0 animate-fade-in-up" style={{ animationDelay: `${0.1 * i}s` }}>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
                  <InlineEdit contentKey={`${item.key}_year`} defaultValue={item.year} className="text-xs font-bold text-cyan-400" />
                </div>
                {i < timeline.length - 1 && (
                  <div className="w-px flex-1 bg-gradient-to-b from-cyan-500/30 to-transparent mt-2" />
                )}
              </div>
              <div className="pt-2">
                <InlineEdit contentKey={`${item.key}_title`} defaultValue={item.title} as="h4" className="text-base font-semibold text-white mb-1" />
                <InlineEdit contentKey={`${item.key}_desc`} defaultValue={item.desc} as="p" className="text-sm text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding py-20">
        <div className="card !p-10 sm:!p-14 text-center bg-gradient-to-br from-cyan-500/5 to-emerald-500/5">
          <InlineEdit
            contentKey="about_cta_title"
            defaultValue="Envie de travailler avec nous ?"
            as="h2"
            className="text-2xl sm:text-3xl font-bold text-white mb-3"
          />
          <InlineEdit
            contentKey="about_cta_desc"
            defaultValue="Discutons de vos besoins IT lors d'un premier échange gratuit et sans engagement."
            as="p"
            className="text-slate-400 max-w-lg mx-auto mb-6"
            multiline
          />
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/rendez-vous" className="btn-primary">
              Prendre rendez-vous
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
